import React, { KeyboardEventHandler, MouseEvent, MouseEventHandler } from "react";
import Icon from "src/components/icon";
import { isRunTime } from "src/utils/isRunTime";
import { addListener, removeListener } from "src/utils/events";
import { scrollTo } from "src/utils/scroll";

class SearchWidgetTab extends React.Component<{
    icon: React.ReactNode;
    label: React.ReactNode;
    children?: React.ReactNode;
    desktopLabelClassname?: string;
    mobileLabelClassname?: string;

    /** provided by search widget tabs component: */
    device?: "mobile" | "desktop";
    renderDesktopContent?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
    isActive?: boolean;
    onTabClick?: MouseEventHandler<HTMLDivElement> | undefined;
}> {

    desktopDiv = React.createRef<HTMLDivElement>();

    onKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (e.nativeEvent.code === "Space" || e.nativeEvent.code === "Enter") {
            e.preventDefault();
            this.scrollIntoView();
            (this.props.onTabClick as any)(e);
        }
    }

    scrollIntoView() {
        if (!this.desktopDiv.current) return;
        const yOffset = -150;
        const y = this.desktopDiv.current.getBoundingClientRect().top + window.scrollY + yOffset;
        scrollTo({top: y, behavior: 'auto'});
    }

    renderMobile() {
        const classes = [];
        if (!this.props.isActive) classes.push('bg-[color:var(--mobile-inactive-tab-bg-color)] text-[color:var(--mobile-inactive-tab-color)]')
        if (this.props.isActive) classes.push('bg-[color:var(--mobile-active-tab-bg-color)] text-[color:var(--mobile-active-tab-color)]')

        /* first / last rounding classes */
        if (this.props.isFirst) classes.push('rounded-t-2xl');
        if (this.props.isLast && !this.props.isActive) classes.push('rounded-b-2xl');

        return <div>
            <div
                role="tab" tabIndex={0} onClick={this.props.onTabClick} onKeyDown={this.onKeyDown}
                className={`w-full h-14 p-4 justify-between items-center inline-flex ${classes.join(" ")} ring-transparent outline-none focus-visible:bg-[color:var(--active-focus-bgcolor)] focus-visible:text-[color:var(--active-focus-color)]`}
            >
                <div className="h-6 justify-start items-center gap-2 flex">
                    <div className="w-6 h-6 relative">{this.props.icon}</div>
                    <div className={this.props.mobileLabelClassname || `text-[15px] font-extrabold font-['Manrope'] leading-[18.80px]`}>{this.props.label}</div>
                </div>
                <Icon className="w-5 h-5 relative" icon={this.props.isActive ? 'Minus' : 'Plus'} />
            </div>
            {this.props.isActive
                ? <div className="contents">
                    <div className="h-[2px] bg-[color:var(--active-tab-line-color)]" />
                    <div className="flex w-full flex-col">
                        {this.props.children}
                    </div>
                </div>
                : null}
        </div>
    }

    renderDesktopTab() {
        const classes = [];

        /* active color classes */
        if (this.props.isActive) {
            classes.push('border-[color:var(--active-tab-line-color)] bg-[color:var(--desktop-active-tab-bg-color)] text-[color:var(--desktop-active-tab-text-color)]');
        } else {
            classes.push('border-transparent bg-[color:var(--desktop-inactive-tab-bg-color)] text-[color:var(--desktop-inactive-tab-text-color)]');
        }

        /* first / last rounding classes */
        if (this.props.isFirst) classes.push('rounded-tl-2xl');
        if (this.props.isLast) classes.push('rounded-tr-2xl');

        return <>
            <div
                ref={this.desktopDiv}
                role="tab" tabIndex={0} onClick={this.props.onTabClick} onKeyDown={this.onKeyDown}
                className={`flex-1 h-[88px] p-6 ${classes.join(" ")} border-b-2 flex-col justify-center items-center gap-2 inline-flex cursor-pointer ring-transparent outline-none focus-visible:bg-[color:var(--active-focus-bgcolor)] focus-visible:text-[color:var(--active-focus-color)]`}
            >
                <div className="w-6 h-6 relative">{this.props.icon}</div>
                <div className={this.props.desktopLabelClassname || `text-[15px] font-extrabold font-['Manrope'] leading-[18.80px]`}>{this.props.label}</div>
            </div>
        </>
    }

    renderDesktopContent() {
        return <>
            <div className="contents">
                {this.props.children}
            </div>
        </>
    }

    render() {
        if (this.props.renderDesktopContent) return this.renderDesktopContent();
        if (this.props.device === "mobile") return this.renderMobile();
        return this.renderDesktopTab();
    }

}

type SearchWidgetTabsProps = {
    initialTab?: number;
    onTabChange?: (x:number) => void;
    activeFocusBackgroundColor?: string;
    activeFocusTextColor?: string;
    activeTabLineColor?: string;
    desktopActiveTabTextColor?: string;
    desktopActiveTabBackgroundColor?: string;
    desktopInactiveTabTextColor?: string;
    desktopInactiveTabBackgroundColor?: string;
    desktopBackgroundColor?: string;
    mobileInactiveTabTextColor?: string;
    mobileInactiveTabBackgroundColor?: string;
    mobileActiveTabTextColor?: string;
    mobileActiveTabBackgroundColor?: string;
    mobileBackgroundColor?: string;
    children: React.ReactNode;
}

type SearchWidgetTabsState = {
    activeTab: number;
    device: "desktop" | "mobile";
}

export default class SearchWidgetTabs extends React.Component<SearchWidgetTabsProps, SearchWidgetTabsState> {

    static MOBILE_WIDTH = 1024;

    static Tab = SearchWidgetTab;

    public state: SearchWidgetTabsState = {
        activeTab: this.props.initialTab || 0,
        device: isRunTime() ? (window.innerWidth >= SearchWidgetTabs.MOBILE_WIDTH ? "desktop" : "mobile") : "desktop",
    }

    private onResize = () => {
        if (this.state.device === 'desktop' && window.innerWidth < SearchWidgetTabs.MOBILE_WIDTH) return this.setState({device: 'mobile'});
        if (this.state.device === 'mobile' && window.innerWidth >= SearchWidgetTabs.MOBILE_WIDTH) return this.setState({device: 'desktop'});
    }

    componentDidMount() {
        addListener('resize', this.onResize);
    }

    componentWillUnmount(): void {
        removeListener('resize', this.onResize);
    }

    get style(): any {
        return {
            '--active-focus-bgcolor': this.props.activeFocusBackgroundColor || '#6D62E4',
            '--active-focus-color': this.props.activeFocusTextColor || '#FFF',
            '--active-tab-line-color': this.props.activeTabLineColor || '#6D62E4',
            '--desktop-active-tab-text-color': this.props.desktopActiveTabTextColor || '#6D62E4',
            '--desktop-active-tab-bg-color': this.props.desktopActiveTabBackgroundColor || '#FFF',
            '--desktop-inactive-tab-text-color': this.props.desktopInactiveTabTextColor || '#242426',
            '--desktop-inactive-tab-bg-color': this.props.desktopInactiveTabBackgroundColor || '#EAEAF2',
            '--desktop-background-color': this.props.desktopBackgroundColor || '#FFF',
            '--mobile-inactive-tab-color': this.props.mobileInactiveTabTextColor || '#242426',
            '--mobile-inactive-tab-bg-color': this.props.mobileInactiveTabBackgroundColor || '#EAEAF2',
            '--mobile-active-tab-color': this.props.mobileActiveTabTextColor || '#242426',
            '--mobile-active-tab-bg-color': this.props.mobileActiveTabBackgroundColor || '#FFF',
            '--mobile-background-color': this.props.mobileBackgroundColor || '#FFF',
        }
    }

    changeTab(newtab: number) {
        this.setState({activeTab: newtab}, () => { if (this.props.onTabChange) this.props.onTabChange(newtab); });
    }


    render(): React.ReactNode {
        const children = React.Children.toArray(this.props.children);
        const numchilds = children.length;
        /** Render children, but augment their props: */
        const mapped = React.Children.map(children, (child, index) => {
            if (React.isValidElement(child)) return React.cloneElement(child, {
                isActive: this.state.activeTab === index,
                isFirst: index === 0,
                isLast: index === numchilds - 1,
                device: this.state.device,
                onTabClick: (ev: MouseEvent<HTMLDivElement>) => this.changeTab(index)
            } as any);
            return child;
        });

        /* Mobile view */
        if (this.state.device === "mobile") {
            return <div style={this.style} className="border-1 rounded-2xl shadow bg-[color:var(--mobile-background-color)]">{mapped}</div>
        }

        /* Desktop view: extra Property for tab component */
        const currentActiveChild = mapped[this.state.activeTab];
        const activeChildContents = React.Children.map([currentActiveChild], (child, index) => {
            if (React.isValidElement(child)) return React.cloneElement(child, { renderDesktopContent: true } as any);
            return child;
        });

        return <div style={this.style} className="border-1 rounded-2xl shadow">
            <div role="tablist" className="flex flex-row">
                {mapped}
            </div>
            <div className="container rounded-b-2xl bg-[color:var(--desktop-background-color)] min-w-full">
                {activeChildContents}
            </div>
        </div>
    }
}